// Aba "Acessos" do painel Aplicativo (Marketing; até 14/09/2026 era a aba "App TWMS"
// do Tecnologia) — a fila de primeiro acesso do
// app.twms.com.br (plataforma da consultora, stack /opt/stacks/app-twms).
// Backend: server/routes/app-twms.cjs, proxy admin → app-twms-backend:3006.
//
// Decisão do dono (14/09/2026): a consultora pede (código + CPF + WhatsApp), o
// escritório aprova aqui, e o app manda uma senha de 6 números no WhatsApp.
// Esqueceu a senha? Isso NÃO passa por aqui: quem já foi aprovada recebe uma
// nova sozinha, no número do cadastro.
//
// O que só um humano decide nesta tela: para QUAL número a senha vai quando o do
// cadastro falta ou é diferente do que ela informou. O botão padrão manda para o
// cadastro; mandar para o informado é um segundo botão, com confirmação, porque
// é justamente o caminho de quem sabe o CPF de outra pessoa.
//
// 15/09/2026: e quando NENHUM dos dois serve (cadastro velho, ela digitou
// errado), "Outro número…" deixa o escritório digitar o WhatsApp. Todo envio
// para número que não é o do cadastro vira o WhatsApp do app dela — o
// "esqueci" seguinte vai para ele, não para o número morto.
//
// 17/09/2026: a senha CHEGOU? Cada aprovado mostra o que a Meta respondeu
// (lida · entregue · sem confirmação · NÃO CHEGOU, com o motivo), o topo avisa
// quantas não chegaram, e a lista se atualiza sozinha a cada 20 s com a aba
// visível — o escritório era cobrado por senha que falhou sem ninguém ver.
// Regra em backend/code/lib/app-twms-entrega.cjs.
import { useState, useEffect, useCallback, useRef } from 'react'
import {
  FiRefreshCw, FiLoader, FiAlertCircle, FiUsers, FiKey, FiInbox, FiCheck, FiX,
  FiAlertTriangle, FiSend, FiSearch, FiEye, FiEyeOff, FiCopy, FiCheckSquare, FiSquare, FiXCircle,
  FiSmartphone, FiPlus, FiTrash2, FiRotateCcw,
} from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import { formatPhone, dataHoraCurta, soDigitos } from '../lib/mascaras'
import { FotoPessoa } from './foto/FotoPessoa'

type Status = 'pendente' | 'aprovada' | 'recusada'

type Pedido = {
  id: number
  codigo: string
  status: 'pendente' | 'enviando' | 'aprovada' | 'recusada'
  criado_em: string
  decidido_em: string | null
  decidido_por: string | null
  destino_origem: 'cadastro' | 'informado' | 'escritorio' | null
  motivo: string | null
  erro_envio: string | null
  nome: string | null
  papel: string | null
  grupo: string | null
  distrito: string | null
  teste: boolean
  tem_direito: boolean
  whatsapp_cadastro: string | null
  whatsapp_informado: string | null
  whatsapp_confere: boolean
  // 17/09/2026 — o aviso no WhatsApp do escritório (o que leva os botões).
  aviso: { em: string; para: string[]; ok: boolean; erro: string | null } | null
  // 16/09/2026 — a situação da senha (só faz sentido depois de aprovado).
  whatsapp_app: string | null
  conta_ativa: boolean | null
  entrou_em: string | null
  senha_provisoria: boolean
  ultimo_envio: {
    em: string; fim: string | null; origem: string | null; motivo: string | null
    // 17/09/2026 — o que a Meta respondeu (o CRM casa pelo wamid). null = sem balão no CRM.
    entrega?: { status: string | null; erro_codigo: number | null; erro: string | null } | null
  } | null
  situacao?: Situacao | null
}

type Situacao = 'entrou' | 'nao_chegou' | 'chegou_sem_entrar' | 'aguardando' | 'sem_confirmacao'
type Filtro = 'todos' | 'nao_chegou' | 'sem_confirmacao' | 'chegou_sem_entrar'
type Alertas = { nao_chegou: number; sem_confirmacao: number; chegou_sem_entrar: number }

type Dados = {
  status: Status
  resumo: { com_direito: number; com_senha: number; pendentes: number; aprovadas: number; recusadas: number; alertas?: Alertas }
  pedidos: Pedido[]
  entrega_indisponivel?: boolean
}

const ATUALIZA_S = 20

const PAPEL: Record<string, string> = {
  distribuicao: 'Distribuição', empresario: 'Empresário', lider: 'Líder', consultora: 'Consultora',
}

// O app grava o WhatsApp com DDI (5567...). O formatPhone do CRM assume número
// nacional e leria o 55 como DDD — mesmo cuidado do relatorioFinder.
const fone = (v: string | null) => {
  if (!v) return '—'
  const d = v.replace(/\D/g, '')
  return formatPhone(d.length > 11 && d.startsWith('55') ? d.slice(2) : d)
}

const num = (n: number) => n.toLocaleString('pt-BR')

/* ── aprovar em LOTE (17/09/2026) ──────────────────────────────────────────── */
// Regra do dono, aplicada no APP (`destino: 'automatico'` em lib/acessos.js): o
// número que ela INFORMOU vem primeiro quando é diferente do cadastro do Draco;
// igual, inválido ou ausente → cadastro. Aqui a tela só PREVÊ para mostrar na
// confirmação — quem decide de verdade é o servidor, pedido a pedido.
const entraNoLote = (p: Pedido) => p.status === 'pendente' && p.tem_direito && Boolean(p.whatsapp_informado || p.whatsapp_cadastro)
const vaiParaInformado = (p: Pedido) => Boolean(p.whatsapp_informado && p.whatsapp_informado !== p.whatsapp_cadastro)
type ResultadoLote = { id: number; nome: string; ok: boolean; texto: string }

// `podeGerir`: sem a permissão `aplicativo_gestao` a fila aparece sem os botões (o backend recusa de novo).
export function AppTwmsAcessosPanel({ podeGerir = true }: { podeGerir?: boolean } = {}) {
  const { token } = useAuth()
  const [status, setStatus] = useState<Status>('pendente')
  const [filtro, setFiltro] = useState<Filtro>('todos')
  const [dados, setDados] = useState<Dados | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  // Busca por nome ou código (16/09/2026). `busca` é o que foi digitado; `termo`
  // o que vai para o servidor, 350 ms depois de parar de digitar.
  const [busca, setBusca] = useState('')
  const [termo, setTermo] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setTermo(busca.trim()), 350)
    return () => clearTimeout(t)
  }, [busca])

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    const f = status === 'aprovada' && filtro !== 'todos' ? `&filtro=${filtro}` : ''
    try {
      const r = await fetch(`${API_ENDPOINTS.APP_TWMS_ACESSOS}?status=${status}${termo ? `&busca=${encodeURIComponent(termo)}` : ''}${f}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`)
      setDados(j)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falhou')
    } finally {
      setCarregando(false)
    }
  }, [token, status, termo, filtro])

  useEffect(() => { carregar() }, [carregar])
  // As linhas recarregam 1 s DEPOIS de agir: por esta ref pegam o filtro de AGORA,
  // não o do clique (senão trocar de filtro nesse meio-tempo trazia a lista velha).
  const carregarRef = useRef(carregar)
  useEffect(() => { carregarRef.current = carregar }, [carregar])
  const recarregar = useCallback(() => carregarRef.current(), [])

  // ── lote ──
  const [marcados, setMarcados] = useState<Set<number>>(new Set())
  const [confirmaLote, setConfirmaLote] = useState(false)
  const [lote, setLote] = useState<{ feitos: number; total: number; atual: string } | null>(null)
  const [resultados, setResultados] = useState<ResultadoLote[] | null>(null)
  const parar = useRef(false)
  // Trocou de aba ou de busca: a seleção antiga não vale mais.
  useEffect(() => { setMarcados(new Set()); setConfirmaLote(false) }, [status, termo])

  // Tempo real (17/09/2026): recarrega a cada 20 s com a aba VISÍVEL e sem lote
  // andando — o "entregue"/"falhou" da Meta chega segundos depois do envio.
  const loteRef = useRef(false)
  useEffect(() => { loteRef.current = !!lote }, [lote])
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible' && !loteRef.current) carregar()
    }, ATUALIZA_S * 1000)
    const voltou = () => { if (document.visibilityState === 'visible' && !loteRef.current) carregar() }
    document.addEventListener('visibilitychange', voltou)
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', voltou) }
  }, [carregar])

  const verAlerta = (f: Filtro) => { setStatus('aprovada'); setFiltro(f); setBusca(''); setTermo('') }

  const aprovarLote = async (lista: Pedido[]) => {
    parar.current = false
    setConfirmaLote(false); setResultados(null)
    const res: ResultadoLote[] = []
    // UM POR VEZ, de propósito: cada aprovação espera a Meta aceitar o envio, e em
    // paralelo uma falha no meio deixaria metade das pessoas sem saber se foi.
    for (let i = 0; i < lista.length; i++) {
      if (parar.current) break
      const p = lista[i]
      const nome = p.nome || p.codigo
      setLote({ feitos: i, total: lista.length, atual: nome })
      try {
        const r = await fetch(API_ENDPOINTS.APP_TWMS_APROVAR(p.id), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ destino: 'automatico' }),
        })
        const j = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`)
        res.push({ id: p.id, nome, ok: true, texto: `final ••${j.destino} (${j.origem === 'informado' ? 'número informado' : 'cadastro'})` })
      } catch (e) {
        res.push({ id: p.id, nome, ok: false, texto: e instanceof Error ? e.message : 'falhou' })
      }
      setResultados([...res])
    }
    setLote(null); setMarcados(new Set())
    carregar()
  }

  if (carregando && !dados) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <FiLoader className="w-5 h-5 animate-spin" />
      </div>
    )
  }
  if (erro && !dados) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <FiAlertCircle className="w-7 h-7 opacity-40" />
        <p className="text-sm">{erro}</p>
        <button onClick={carregar} className="text-xs underline">tentar de novo</button>
      </div>
    )
  }
  if (!dados) return null

  const { resumo, pedidos } = dados
  const alertas = resumo.alertas
  const semSenha = Math.max(resumo.com_direito - resumo.com_senha, 0)
  const elegiveis = status === 'pendente' && podeGerir ? pedidos.filter(entraNoLote) : []
  const escolhidos = elegiveis.filter(p => marcados.has(p.id))
  const todosMarcados = elegiveis.length > 0 && escolhidos.length === elegiveis.length
  const paraInformado = escolhidos.filter(vaiParaInformado).length
  const marcar = (id: number) => setMarcados(m => { const n = new Set(m); if (n.has(id)) n.delete(id); else n.add(id); return n })

  return (
    <div className="h-full min-h-0 flex flex-col overflow-y-auto gap-4">
      {/* ── cabeçalho ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold">App Luísa — primeiro acesso</h2>
          <p className="text-[11px] text-muted-foreground">
            app.twplanalto.com.br · aprovar manda uma senha de 6 números no WhatsApp da pessoa
          </p>
        </div>
        <button onClick={carregar} disabled={carregando}
                title={`Atualiza sozinho a cada ${ATUALIZA_S} segundos`}
                className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <FiRefreshCw className={`w-3.5 h-3.5 ${carregando ? 'animate-spin' : ''}`} />
          atualizar · automático a cada {ATUALIZA_S} s
        </button>
      </div>

      {/* ── números-chave ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={FiUsers} rotulo="Com direito" valor={num(resumo.com_direito)}
              nota="Draco, fora dos depósitos" />
        <Tile icon={FiKey} rotulo="Com senha" valor={num(resumo.com_senha)}
              nota={`${num(semSenha)} ainda sem acesso`} />
        <Tile icon={FiInbox} rotulo="Na fila" valor={num(resumo.pendentes)}
              nota={resumo.pendentes ? 'esperando aprovação' : 'nada pendente'} />
        <Tile icon={FiCheck} rotulo="Aprovados" valor={num(resumo.aprovadas)}
              nota={`${num(resumo.recusadas)} recusados`} />
      </div>

      {/* ── aprovação automática + recall (25/09/2026) ── */}

      {/* ── aprovar pelo WhatsApp (17/09/2026) ── */}
      <AvisoWhatsapp token={token} podeGerir={podeGerir} />

      {/* ── senhas que não chegaram (17/09/2026) ── */}
      {alertas && alertas.nao_chegou > 0 && (
        <button onClick={() => verAlerta('nao_chegou')}
                className="text-left rounded-2xl border border-red-500/60 bg-red-500/10 px-4 py-3 flex items-center gap-3 flex-wrap">
          <FiXCircle className="w-5 h-5 text-red-600 shrink-0" />
          <span className="text-[13px] flex-1 min-w-[14rem]">
            <b className="text-red-700">{num(alertas.nao_chegou)} {alertas.nao_chegou === 1 ? 'senha NÃO chegou' : 'senhas NÃO chegaram'}</b>
            {' '}— a Meta recusou a entrega e a pessoa ainda não entrou. Mande para outro número.
          </span>
          <span className="text-[12px] font-semibold text-red-700 underline">ver quem</span>
        </button>
      )}
      {alertas && alertas.sem_confirmacao > 0 && (
        <button onClick={() => verAlerta('sem_confirmacao')}
                className="text-left rounded-2xl border border-amber-500/50 bg-amber-500/10 px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <FiAlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
          <span className="text-[12px] flex-1 min-w-[14rem]">
            <b>{num(alertas.sem_confirmacao)}</b> sem confirmação de entrega há mais de 10 min e ainda sem entrar
            {' '}<span className="text-muted-foreground">(pode ter chegado — confirme com a líder antes de reenviar)</span>
          </span>
          <span className="text-[12px] font-semibold underline">ver quem</span>
        </button>
      )}
      {dados.entrega_indisponivel && (
        <p className="text-[11.5px] text-amber-700">Não deu para ler agora se as senhas chegaram. A lista segue, sem essa informação.</p>
      )}

      {erro && (
        <p className="text-[12px] text-red-600 flex items-center gap-1.5">
          <FiAlertCircle className="w-3.5 h-3.5" />{erro}
        </p>
      )}

      {/* ── a fila ────────────────────────────────────────────────────────── */}
      <section className="glass-panel neu-card rounded-2xl p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-[12px] font-semibold">
              {status === 'pendente' ? 'Pedidos esperando' : status === 'aprovada' ? 'Aprovados' : 'Recusados'}
              {' '}({pedidos.length})
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {status === 'pendente'
                ? 'do mais antigo para o mais novo · confira nome, grupo e WhatsApp antes de aprovar'
                : 'decisões mais recentes primeiro'}
            </p>
          </div>
          <label className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 flex-1 min-w-[12rem] max-w-xs">
            <FiSearch className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="buscar por nome ou código"
                   aria-label="Buscar por nome ou código"
                   className="w-full bg-transparent text-[12px] outline-none" />
            {busca && (
              <button onClick={() => setBusca('')} aria-label="Limpar a busca" className="text-muted-foreground">
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </label>
          <div className="flex rounded-lg overflow-hidden border border-border">
            {([['pendente', 'Na fila'], ['aprovada', 'Aprovados'], ['recusada', 'Recusados']] as [Status, string][]).map(([k, t]) => (
              <button key={k} onClick={() => { setStatus(k); setFiltro('todos') }}
                      className={`px-2.5 py-1 text-[11px] font-medium ${
                        status === k ? 'bg-foreground/10 text-foreground' : 'text-muted-foreground'
                      }`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* ── filtro pela ENTREGA da senha (17/09/2026) ── */}
        {status === 'aprovada' && (
          <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Filtrar pela entrega da senha">
            {([
              ['todos', 'Todos', null, ''],
              ['nao_chegou', 'Não chegou', alertas?.nao_chegou, 'border-red-500/60 text-red-700'],
              ['sem_confirmacao', 'Sem confirmação', alertas?.sem_confirmacao, 'border-amber-500/60 text-amber-800'],
              ['chegou_sem_entrar', 'Chegou, não entrou', alertas?.chegou_sem_entrar, ''],
            ] as [Filtro, string, number | null | undefined, string][]).map(([k, t, n, cor]) => (
              <button key={k} onClick={() => setFiltro(k)} aria-pressed={filtro === k}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                        filtro === k ? 'bg-foreground text-background border-foreground' : `${cor || 'border-border text-muted-foreground'}`
                      }`}>
                {t}{n != null && <span className="tabular-nums"> ({num(n)})</span>}
              </button>
            ))}
          </div>
        )}

        {/* ── aprovar em lote ── */}
        {elegiveis.length > 0 && (
          <div className="rounded-lg border border-border px-3 py-2 flex flex-col gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => setMarcados(todosMarcados ? new Set() : new Set(elegiveis.map(p => p.id)))}
                      disabled={!!lote}
                      className="inline-flex items-center gap-1.5 text-[11.5px] font-medium disabled:opacity-50">
                {todosMarcados ? <FiCheckSquare className="w-4 h-4" /> : <FiSquare className="w-4 h-4" />}
                {todosMarcados ? 'Desmarcar todos' : `Selecionar todos (${num(elegiveis.length)})`}
              </button>
              <button onClick={() => setConfirmaLote(true)} disabled={!escolhidos.length || !!lote}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-40">
                <FiCheck className="w-3.5 h-3.5" />Aprovar em lote ({num(escolhidos.length)})
              </button>
              <span className="text-[11px] text-muted-foreground">
                Se ela informou um WhatsApp diferente do cadastro, a senha vai para o informado.
              </span>
            </div>

            {confirmaLote && !lote && (
              <div className="flex items-center gap-2 flex-wrap rounded-lg bg-amber-500/10 border border-amber-500/40 px-3 py-2">
                <span className="text-[12px]">
                  Aprovar <b>{num(escolhidos.length)}</b> {escolhidos.length === 1 ? 'pedido' : 'pedidos'} e mandar a senha provisória:{' '}
                  <b>{num(paraInformado)}</b> para o número informado por ela, <b>{num(escolhidos.length - paraInformado)}</b> para o cadastro.
                </span>
                <button onClick={() => aprovarLote(escolhidos)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3 py-1.5 text-[11.5px] font-semibold">
                  <FiSend className="w-3.5 h-3.5" />Confirmar e enviar
                </button>
                <button onClick={() => setConfirmaLote(false)} className="text-[11.5px] text-muted-foreground underline">cancelar</button>
              </div>
            )}
          </div>
        )}

        {lote && (
          <div className="rounded-lg border border-border px-3 py-2 flex items-center gap-3 flex-wrap">
            <FiLoader className="w-4 h-4 animate-spin" />
            <span className="text-[12px]">Enviando {num(lote.feitos + 1)} de {num(lote.total)} — {lote.atual}</span>
            <div className="h-1.5 flex-1 min-w-[8rem] rounded-full bg-foreground/10 overflow-hidden">
              <div className="h-full bg-foreground/70 transition-all" style={{ width: `${Math.round((lote.feitos / lote.total) * 100)}%` }} />
            </div>
            <button onClick={() => { parar.current = true }} className="text-[11.5px] text-muted-foreground underline">parar depois deste</button>
          </div>
        )}

        {resultados && !lote && (
          <div className="rounded-lg border border-border px-3 py-2 flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-semibold">
                Lote: {num(resultados.filter(r => r.ok).length)} {resultados.filter(r => r.ok).length === 1 ? 'aprovado' : 'aprovados'}
                {resultados.some(r => !r.ok) && <span className="text-red-600"> · {num(resultados.filter(r => !r.ok).length)} com falha (continuam na fila)</span>}
              </span>
              <button onClick={() => setResultados(null)} aria-label="Fechar o resultado" className="text-muted-foreground"><FiX className="w-3.5 h-3.5" /></button>
            </div>
            <ul className="text-[11.5px] flex flex-col gap-0.5 max-h-48 overflow-y-auto">
              {resultados.map(r => (
                <li key={r.id} className={r.ok ? 'text-muted-foreground' : 'text-red-600'}>
                  {r.ok ? '✓' : '✗'} {r.nome} — {r.texto}
                </li>
              ))}
            </ul>
          </div>
        )}

        {pedidos.length === 0 ? (
          <p className="text-[12px] text-muted-foreground py-6 text-center">
            {termo ? `Ninguém com “${termo}” nesta lista.` : status === 'pendente' ? 'Nenhum pedido esperando.'
              : status === 'aprovada' && filtro === 'nao_chegou' ? 'Nenhuma senha deixou de chegar. ✓'
              : status === 'aprovada' && filtro !== 'todos' ? 'Ninguém nesta situação.' : 'Nada por aqui ainda.'}
          </p>
        ) : (
          <div className="flex flex-col divide-y divide-border/60">
            {pedidos.map((p) => (
              <Linha key={p.id} p={p} token={token} aoDecidir={recarregar} podeGerir={podeGerir}
                     marcado={entraNoLote(p) && podeGerir && status === 'pendente' ? marcados.has(p.id) : undefined}
                     aoMarcar={() => marcar(p.id)} travado={!!lote} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

/* ── um pedido ─────────────────────────────────────────────────────────────── */

function Linha({ p, token, aoDecidir, podeGerir, marcado, aoMarcar, travado }: {
  p: Pedido; token: string | null; aoDecidir: () => void; podeGerir: boolean
  // `marcado` undefined = a linha não entra no lote (sem caixa).
  marcado?: boolean; aoMarcar?: () => void; travado?: boolean
}) {
  const [indo, setIndo] = useState(false)
  const [erro, setErro] = useState<string | null>(p.erro_envio ? `último envio falhou: ${p.erro_envio}` : null)
  const [ok, setOk] = useState<string | null>(null)
  // Confirmação em DOIS cliques, na própria linha, para o caminho arriscado.
  const [confirmaInformado, setConfirmaInformado] = useState(false)
  const [outro, setOutro] = useState(false)
  const [outroZap, setOutroZap] = useState('')
  const [recusando, setRecusando] = useState(false)
  const [motivo, setMotivo] = useState('')

  const decidir = async (acao: 'aprovar' | 'recusar', corpo: Record<string, string>) => {
    setIndo(true); setErro(null)
    try {
      const url = acao === 'aprovar'
        ? API_ENDPOINTS.APP_TWMS_APROVAR(p.id) : API_ENDPOINTS.APP_TWMS_RECUSAR(p.id)
      const r = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`)
      setOk(acao === 'aprovar'
        ? `senha enviada para o final ••${j.destino}${j.origem === 'escritorio' ? ' — ficou como o WhatsApp dela no app' : ''}`
        : 'recusado')
      // Um respiro para a pessoa ver o "enviada" antes de a linha sair da fila.
      setTimeout(aoDecidir, 900)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falhou')
      setIndo(false)
    }
  }

  const pendente = p.status === 'pendente' || p.status === 'enviando'
  // Recusado também pode ser aprovado depois (16/09/2026): os mesmos botões da fila.
  const decidivel = pendente || p.status === 'recusada'
  const semCadastro = !p.whatsapp_cadastro
  const diferente = !semCadastro && !p.whatsapp_confere

  return (
    <div className={`py-3 flex flex-col gap-2 ${p.situacao === 'nao_chegou' ? 'border-l-4 border-red-500 pl-3 -ml-1' : ''}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex items-center gap-3">
          {marcado !== undefined && (
            <input type="checkbox" checked={marcado} onChange={aoMarcar} disabled={travado}
                   aria-label={`Selecionar ${p.nome || p.codigo} para aprovar em lote`}
                   className="w-4 h-4 shrink-0 accent-current" />
          )}
          {/* A foto ajuda a conferir quem pede — se ela já pôs uma no app. */}
          <FotoPessoa codigo={p.codigo} nome={p.nome} tamanho={40} />
          <div className="min-w-0">
          <p className="text-[13px] font-semibold truncate">
            {p.nome || '(não encontrado no Draco)'}
            {p.teste && <span className="ml-2 text-[10px] font-medium text-muted-foreground">TESTE</span>}
          </p>
          <p className="text-[11px] text-muted-foreground">
            <span className="tabular-nums">{p.codigo}</span>
            {p.papel && <> · {PAPEL[p.papel] || p.papel}</>}
            {p.grupo && <> · {p.grupo}</>}
            {p.distrito && <> · {p.distrito}</>}
          </p>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground sm:shrink-0">
          pediu {dataHoraCurta(p.criado_em)}
          {!pendente && p.decidido_em && <> · {p.status === 'aprovada' ? 'aprovado' : 'recusado'} {dataHoraCurta(p.decidido_em)}{p.decidido_por && <> por {p.decidido_por}</>}</>}
        </p>
      </div>

      {/* Os dois números lado a lado: é a conferência que este painel existe para fazer. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
        <div className="rounded-lg border border-border px-3 py-2">
          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">WhatsApp do cadastro</span>
          <span className="tabular-nums font-medium">{fone(p.whatsapp_cadastro)}</span>
        </div>
        <div className={`rounded-lg border px-3 py-2 ${diferente || semCadastro ? 'border-amber-500/60' : 'border-border'}`}>
          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">Informado por ela</span>
          <span className="tabular-nums font-medium">{fone(p.whatsapp_informado)}</span>
          {p.whatsapp_confere && <span className="ml-2 text-[10px] text-emerald-700 font-semibold">confere</span>}
          {diferente && <span className="ml-2 text-[10px] text-amber-700 font-semibold">diferente do cadastro</span>}
          {semCadastro && <span className="ml-2 text-[10px] text-amber-700 font-semibold">cadastro sem WhatsApp</span>}
        </div>
      </div>

      {/* Prova de que o aviso saiu (17/09/2026): ele não aparece nas Conversas
          — é recado de escritório, não conversa de cliente — então aparece aqui. */}
      {p.aviso && (
        <p className={`text-[11px] flex items-center gap-1.5 ${p.aviso.ok ? 'text-muted-foreground' : 'text-red-600'}`}>
          <FiSmartphone className="w-3.5 h-3.5 shrink-0" />
          {p.aviso.ok
            ? `Avisado no WhatsApp ${dataHoraCurta(p.aviso.em)} — ${p.aviso.para.length === 1 ? 'final' : 'finais'} ${p.aviso.para.join(', ')}`
            : `O aviso no WhatsApp NÃO saiu: ${p.aviso.erro}`}
        </p>
      )}

      {!p.tem_direito && pendente && (
        <p className="text-[11.5px] text-amber-700 flex items-center gap-1.5">
          <FiAlertTriangle className="w-3.5 h-3.5 shrink-0" />
          Este cadastro não tem mais direito ao app (saiu do Draco ou foi para um distrito de depósito). Só dá para recusar.
        </p>
      )}
      {!pendente && p.status === 'aprovada' && p.destino_origem === 'informado' && (
        <p className="text-[11px] text-amber-700">A senha foi para o número INFORMADO, não para o do cadastro.</p>
      )}
      {!pendente && p.status === 'aprovada' && p.destino_origem === 'escritorio' && (
        <p className="text-[11px] text-amber-700">A senha foi para um número DIGITADO pelo escritório, não para o do cadastro.</p>
      )}
      {!pendente && p.motivo && <p className="text-[11px] text-muted-foreground">Motivo: {p.motivo}</p>}

      {erro && <p className="text-[11.5px] text-red-600">{erro}</p>}
      {ok && <p className="text-[11.5px] text-emerald-700 font-medium">{ok}</p>}

      {!pendente && p.status === 'aprovada' && (
        <SenhaAprovada p={p} token={token} podeGerir={podeGerir} aoMudar={aoDecidir} />
      )}

      {decidivel && !ok && podeGerir && (
        recusando ? (
          <div className="flex items-center gap-2 flex-wrap">
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={200}
                   placeholder="motivo (opcional, fica só aqui)"
                   className="flex-1 min-w-[12rem] rounded-lg border border-border bg-transparent px-2.5 py-1.5 text-[12px]" />
            <button onClick={() => decidir('recusar', { motivo })} disabled={indo}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/60 text-red-700 px-2.5 py-1.5 text-[11.5px] font-medium disabled:opacity-50">
              <FiX className="w-3.5 h-3.5" />Confirmar recusa
            </button>
            <button onClick={() => setRecusando(false)} disabled={indo}
                    className="text-[11.5px] text-muted-foreground underline">cancelar</button>
          </div>
        ) : outro ? (
          <div className="flex items-center gap-2 flex-wrap">
            <input value={outroZap} onChange={(e) => setOutroZap(formatPhone(e.target.value))} inputMode="tel" autoFocus
                   placeholder="WhatsApp novo, com DDD"
                   className="w-[11.5rem] rounded-lg border border-border bg-transparent px-2.5 py-1.5 text-[12px] tabular-nums" />
            <button onClick={() => decidir('aprovar', { destino: 'outro', celular: soDigitos(outroZap) })}
                    disabled={indo || soDigitos(outroZap).length < 10}
                    title="Só se você conferiu com a pessoa que o número é dela"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500 text-amber-800 px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-40">
              {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />}
              Aprovar e enviar para este número
            </button>
            <button onClick={() => { setOutro(false); setOutroZap('') }} disabled={indo}
                    className="text-[11.5px] text-muted-foreground underline">cancelar</button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            {p.tem_direito && (
              <button onClick={() => decidir('aprovar', { destino: 'cadastro' })}
                      disabled={indo || semCadastro}
                      title={semCadastro ? 'O cadastro não tem WhatsApp — use o número informado ou recuse' : undefined}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-40">
                {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheck className="w-3.5 h-3.5" />}
                {p.status === 'recusada' ? 'Aprovar agora e enviar senha' : 'Aprovar e enviar senha'}{!semCadastro && ' (cadastro)'}
              </button>
            )}
            {p.tem_direito && (diferente || semCadastro) && p.whatsapp_informado && (
              confirmaInformado ? (
                <button onClick={() => decidir('aprovar', { destino: 'informado' })} disabled={indo}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500 text-amber-800 px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-50">
                  <FiSend className="w-3.5 h-3.5" />Confirmo: enviar para {fone(p.whatsapp_informado)}
                </button>
              ) : (
                <button onClick={() => setConfirmaInformado(true)} disabled={indo}
                        title="Só se você conferiu com a pessoa que o número é dela"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[11.5px] font-medium disabled:opacity-50">
                  <FiSend className="w-3.5 h-3.5" />Enviar para o número informado…
                </button>
              )
            )}
            {p.tem_direito && (
              <button onClick={() => { setOutro(true); setConfirmaInformado(false) }} disabled={indo}
                      title="O número do cadastro é antigo e o informado também não serve"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[11.5px] font-medium disabled:opacity-50">
                <FiSend className="w-3.5 h-3.5" />Outro número…
              </button>
            )}
            {pendente && (
              <button onClick={() => setRecusando(true)} disabled={indo}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[11.5px] font-medium text-muted-foreground disabled:opacity-50">
                <FiX className="w-3.5 h-3.5" />Recusar
              </button>
            )}
          </div>
        )
      )}
    </div>
  )
}

/* ── depois de aprovado: reenviar e revelar (16/09/2026) ─────────────────── */
/**
 * A senha que sai pelo WhatsApp é PROVISÓRIA: vale até a pessoa criar a dela no
 * primeiro acesso. Enquanto isso, "Revelar senha" mostra os 6 números para o
 * escritório passar por telefone a quem diz que não chegou — e o app registra
 * quem viu. Criada a própria senha, não há mais o que revelar: aí é reenviar.
 */
const ORIGEM: Record<string, string> = {
  cadastro: 'WhatsApp do cadastro', informado: 'número informado por ela', escritorio: 'número do app, trocado pelo escritório',
}

/** O que a Meta disse da última senha, numa etiqueta. */
function Entrega({ p }: { p: Pedido }) {
  const st = p.ultimo_envio?.entrega?.status
  switch (p.situacao) {
    case 'nao_chegou':
      return <span className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2 py-0.5 text-[10.5px] font-bold">
        <FiXCircle className="w-3 h-3" />NÃO CHEGOU</span>
    case 'sem_confirmacao':
      return <span className="rounded-full border border-amber-500/60 text-amber-800 px-2 py-0.5 text-[10.5px] font-semibold"
                   title="A Meta aceitou o envio mas não confirmou a entrega. Pode ter chegado: às vezes a confirmação se perde.">
        ✓ enviada · sem confirmação</span>
    case 'aguardando':
      return <span className="rounded-full border border-border text-muted-foreground px-2 py-0.5 text-[10.5px] font-medium">✓ enviada agora · aguardando a Meta</span>
    case 'chegou_sem_entrar':
    case 'entrou':
      if (st === 'read') return <span className="rounded-full bg-sky-500/15 text-sky-700 px-2 py-0.5 text-[10.5px] font-semibold">✓✓ lida</span>
      if (st === 'delivered') return <span className="rounded-full bg-emerald-500/15 text-emerald-700 px-2 py-0.5 text-[10.5px] font-semibold">✓✓ entregue</span>
      return null
    default:
      return null
  }
}

function SenhaAprovada({ p, token, podeGerir, aoMudar }: { p: Pedido; token: string | null; podeGerir: boolean; aoMudar: () => void }) {
  const [modo, setModo] = useState<null | 'reenviar' | 'outro' | 'sugerido' | 'refazer'>(null)
  const [sugerido, setSugerido] = useState<{ rotulo: string; numero: string } | null>(null)
  const [zap, setZap] = useState('')
  const [indo, setIndo] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null)
  const [revelada, setRevelada] = useState<{ senha: string; enviada_em: string; destino: string | null } | null>(null)
  const [copiou, setCopiou] = useState(false)

  const proxima = p.whatsapp_app || p.whatsapp_cadastro
  const envio = p.ultimo_envio
  // Para onde foi a última senha, e os OUTROS números que conhecemos dela (17/09/2026):
  // quando não chegou, o botão já oferece o do cadastro ou o que ela informou.
  const foiPara = envio?.origem === 'cadastro' ? p.whatsapp_cadastro : (p.whatsapp_app || p.whatsapp_informado)
  const problema = p.situacao === 'nao_chegou' || p.situacao === 'sem_confirmacao'
  const alternativas = problema
    ? ([['cadastro do Draco', p.whatsapp_cadastro], ['informado por ela', p.whatsapp_informado]] as [string, string | null][])
        .filter((x): x is [string, string] => Boolean(x[1]) && x[1] !== foiPara)
        .filter((x, i, a) => a.findIndex(y => y[1] === x[1]) === i)
    : []

  const post = async (acao: 'senha-nova' | 'revelar-senha' | 'refazer-acesso' | 'reenviar-mesma', corpo: Record<string, string> = {}) => {
    setIndo(true); setMsg(null)
    try {
      const r = await fetch(`/api/app-twms/pessoas/${p.codigo}/${acao}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`)
      return j
    } catch (e) {
      setMsg({ ok: false, t: e instanceof Error ? e.message : 'falhou' })
      return null
    } finally { setIndo(false) }
  }

  const reenviar = async (celular?: string) => {
    const j = await post('senha-nova', celular ? { celular } : {})
    if (!j) return
    setRevelada(null); setModo(null); setZap(''); setSugerido(null)
    setMsg({ ok: true, t: `Senha provisória nova enviada para o final ••${j.destino}${j.origem === 'escritorio' ? ' — ficou como o WhatsApp dela no app' : ''}. A anterior deixou de valer.${j.aviso ? ` ⚠️ ${j.aviso}` : ''}` })
    setTimeout(aoMudar, 1200)
  }

  // A MESMA provisória de novo (25/09/2026): para "sem confirmação" é o socorro
  // certo — uma senha nova mataria a que talvez só esteja atrasada.
  const reenviarMesma = async () => {
    const j = await post('reenviar-mesma')
    if (!j) return
    setModo(null)
    setMsg({ ok: true, t: `A mesma senha provisória foi de novo para o final ••${j.destino}. Ela continua valendo — nada mudou para ela.` })
    setTimeout(aoMudar, 1200)
  }

  const revelar = async () => {
    const j = await post('revelar-senha')
    if (j) setRevelada({ senha: j.senha, enviada_em: j.enviada_em, destino: j.destino })
  }

  /* REFAZER O ACESSO (18/09/2026, pedido do dono): a pessoa volta a ser "nunca
     entrou" e pede senha do zero. Serve para mostrar o caminho inteiro numa
     reunião e para quem se perdeu e prefere recomeçar. Não apaga o que é dela —
     clientes, produtos e pedidos moram no código e no link, não na conta. */
  const refazer = async () => {
    const j = await post('refazer-acesso')
    if (!j) return
    setRevelada(null); setModo(null)
    setMsg({ ok: true, t: `Acesso refeito: ${p.nome || p.codigo} voltou a ser “nunca entrou”. Ela toca em “Pedir senha” no app e o pedido cai aqui na fila. Nada do que ela cadastrou foi apagado.` })
    setTimeout(aoMudar, 2000)
  }

  const copiar = async () => {
    if (!revelada) return
    try { await navigator.clipboard.writeText(revelada.senha); setCopiou(true); setTimeout(() => setCopiou(false), 2000) } catch { /* a senha está na tela */ }
  }

  const botao = 'inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[11.5px] font-medium disabled:opacity-50'

  return (
    <div className={`rounded-lg border px-3 py-2 flex flex-col gap-2 ${p.situacao === 'nao_chegou' ? 'border-red-500/60 bg-red-500/5' : 'border-border'}`}>
      <p className="text-[11.5px] text-muted-foreground flex items-center gap-x-1 gap-y-1 flex-wrap">
        {envio
          ? <>Última senha: <b className="text-foreground">{dataHoraCurta(envio.em)}</b> para o final <b className="text-foreground tabular-nums">••{envio.fim}</b>{envio.origem && <> ({ORIGEM[envio.origem] || envio.origem})</>} <Entrega p={p} /></>
          : 'Nenhuma senha registrada.'}
        {' · '}
        {p.entrou_em ? <>entrou {dataHoraCurta(p.entrou_em)}</> : <span className="text-amber-700 font-medium">ainda não entrou</span>}
        {p.senha_provisoria && <> · <span className="text-sky-700 font-medium">com a senha provisória</span></>}
        {p.conta_ativa === false && <> · <span className="text-red-600 font-medium">acesso desativado</span></>}
      </p>

      {p.situacao === 'nao_chegou' && (
        <p className="text-[12px] text-red-700">
          A senha não chegou no final ••{envio?.fim}{envio?.entrega?.erro ? <> — <b>{envio.entrega.erro}</b></> : <> — a Meta recusou a entrega</>}.
          {' '}{alternativas.length ? 'Mande para outro número dela:' : 'Peça o WhatsApp atual dela e use "Outro número…".'}
        </p>
      )}
      {p.situacao === 'sem_confirmacao' && (
        <p className="text-[11.5px] text-amber-800">
          A Meta não confirmou a entrega e ela ainda não entrou. Pode ter chegado — pergunte à líder antes de mandar outra.
        </p>
      )}

      {revelada && (
        <div className="flex items-center gap-3 flex-wrap rounded-lg bg-foreground/5 px-3 py-2">
          <span className="font-mono text-xl font-semibold tracking-[0.25em] tabular-nums">{revelada.senha}</span>
          <button onClick={copiar} className={botao}><FiCopy className="w-3.5 h-3.5" />{copiou ? 'copiada' : 'copiar'}</button>
          <button onClick={() => setRevelada(null)} className={botao}><FiEyeOff className="w-3.5 h-3.5" />ocultar</button>
          <span className="text-[11px] text-muted-foreground basis-full">
            Provisória: vale até ela criar a própria senha no primeiro acesso. Enviada {dataHoraCurta(revelada.enviada_em)}{revelada.destino && <> para o final ••{revelada.destino}</>}. Sua visualização ficou registrada.
          </span>
        </div>
      )}

      {msg && <p className={`text-[11.5px] ${msg.ok ? 'text-emerald-700 font-medium' : 'text-red-600'}`}>{msg.t}</p>}

      {podeGerir && p.conta_ativa !== false && (
        modo === 'reenviar' ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11.5px]">Mandar uma senha provisória nova para {fone(proxima)}? A anterior deixa de valer.</span>
            <button onClick={() => reenviar()} disabled={indo || !proxima}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-40">
              {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />}Sim, reenviar
            </button>
            <button onClick={() => setModo(null)} disabled={indo} className="text-[11.5px] text-muted-foreground underline">cancelar</button>
          </div>
        ) : modo === 'sugerido' && sugerido ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11.5px]">Mandar uma senha provisória nova para {fone(sugerido.numero)} ({sugerido.rotulo})? Ele passa a ser o WhatsApp dela no app.</span>
            <button onClick={() => reenviar(soDigitos(sugerido.numero))} disabled={indo}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-40">
              {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />}Sim, enviar
            </button>
            <button onClick={() => { setModo(null); setSugerido(null) }} disabled={indo} className="text-[11.5px] text-muted-foreground underline">cancelar</button>
          </div>
        ) : modo === 'refazer' ? (
          <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 flex flex-col gap-2">
            <span className="text-[12px]">
              <b>Refazer o acesso de {p.nome || p.codigo}?</b> Ela volta a ser “nunca entrou”: a conta e os pedidos
              de acesso saem, e ela precisa tocar em <b>Pedir senha</b> no app — o pedido cai aqui na fila para você aprovar.
              A sessão aberta no celular dela é encerrada.
            </span>
            <span className="text-[11.5px] text-muted-foreground">
              Não apaga o que é dela: clientes, produtos, pedidos e o link da vitrine continuam. O histórico de senhas enviadas também.
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={refazer} disabled={indo}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500 text-amber-800 px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-50">
                {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiRotateCcw className="w-3.5 h-3.5" />}Sim, refazer
              </button>
              <button onClick={() => setModo(null)} disabled={indo} className="text-[11.5px] text-muted-foreground underline">cancelar</button>
            </div>
          </div>
        ) : modo === 'outro' ? (
          <div className="flex items-center gap-2 flex-wrap">
            <input value={zap} onChange={(e) => setZap(formatPhone(e.target.value))} inputMode="tel" autoFocus
                   placeholder="WhatsApp novo, com DDD"
                   className="w-[11.5rem] rounded-lg border border-border bg-transparent px-2.5 py-1.5 text-[12px] tabular-nums" />
            <button onClick={() => reenviar(soDigitos(zap))} disabled={indo || soDigitos(zap).length < 10}
                    title="Só se você conferiu com a pessoa que o número é dela"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500 text-amber-800 px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-40">
              {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />}Enviar para este número
            </button>
            <button onClick={() => { setModo(null); setZap('') }} disabled={indo} className="text-[11.5px] text-muted-foreground underline">cancelar</button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            {alternativas.map(([rotulo, numero]) => (
              <button key={numero} onClick={() => { setSugerido({ rotulo, numero }); setModo('sugerido'); setMsg(null) }} disabled={indo}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-50 ${
                        p.situacao === 'nao_chegou' ? 'bg-red-600 text-white' : 'border border-amber-500 text-amber-800'
                      }`}>
                <FiSend className="w-3.5 h-3.5" />Mandar para {fone(numero)} <span className="font-normal opacity-80">({rotulo})</span>
              </button>
            ))}
            {p.senha_provisoria && !p.entrou_em && (
              <button onClick={reenviarMesma} disabled={indo || !proxima}
                      title={proxima ? `A mesma senha, de novo, para ${fone(proxima)} — a anterior continua valendo` : 'Sem WhatsApp — use "Outro número…"'}
                      className={botao}>
                {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />}Reenviar a mesma senha
              </button>
            )}
            <button onClick={() => { setModo('reenviar'); setMsg(null) }} disabled={indo || !proxima}
                    title={proxima ? `Vai para ${fone(proxima)}` : 'Sem WhatsApp — use "Outro número…"'} className={botao}>
              <FiSend className="w-3.5 h-3.5" />Mandar senha nova
            </button>
            <button onClick={() => { setModo('outro'); setMsg(null) }} disabled={indo}
                    title="O número de agora não serve" className={botao}>
              <FiSend className="w-3.5 h-3.5" />Outro número…
            </button>
            {p.senha_provisoria && !revelada && (
              <button onClick={revelar} disabled={indo}
                      title="Mostra os 6 números da senha provisória. Fica registrado quem viu."
                      className={botao}>
                {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiEye className="w-3.5 h-3.5" />}Revelar senha
              </button>
            )}
            <button onClick={() => { setModo('refazer'); setMsg(null) }} disabled={indo}
                    title="Apaga a conta e os pedidos de acesso: ela volta a pedir senha do zero. O que ela cadastrou fica."
                    className={botao}>
              <FiRotateCcw className="w-3.5 h-3.5" />Refazer o acesso
            </button>
          </div>
        )
      )}
    </div>
  )
}

function Tile({ icon: Icon, rotulo, valor, nota }: {
  icon: React.ComponentType<{ className?: string }>
  rotulo: string; valor: string; nota?: string
}) {
  return (
    <div className="glass-panel neu-card rounded-2xl p-4 flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />{rotulo}
      </span>
      <span className="text-2xl font-semibold tabular-nums leading-none">{valor}</span>
      {nota && <span className="text-[11px] text-muted-foreground truncate">{nota}</span>}
    </div>
  )
}


/* ── APROVAR PELO WHATSAPP (17/09/2026) ──────────────────────────────────────
 * Pedido do dono: "amanhã não vou estar no computador". Com a chavinha ligada,
 * cada pedido novo vira uma mensagem no celular de quem está na lista, com os
 * botões Aprovar · Nova senha · Reenviar — e o toque vale como o clique daqui.
 *
 * A chavinha existe porque no escritório ele NÃO quer isso: desligada, não sai
 * aviso nenhum e um toque numa mensagem antiga também não decide nada (elas
 * ficam no celular para sempre, e o botão de ontem continua clicável).
 *
 * Quem está na lista PODE APROVAR ACESSO. Por isso mexer aqui é `aplicativo_gestao`
 * como qualquer outra ação, e o número de quem tocou fica gravado na decisão.
 */
type NumeroAviso = {
  id: number
  numero: string
  nome: string | null
  ativo: boolean
  criado_por: string | null
}

function AvisoWhatsapp({ token, podeGerir }: { token: string | null; podeGerir: boolean }) {
  const [dados, setDados] = useState<{ ligado: boolean; numeros: NumeroAviso[] } | null>(null)
  const [aberto, setAberto] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [nome, setNome] = useState('')
  const [numero, setNumero] = useState('')

  const carregar = useCallback(async () => {
    try {
      const r = await fetch(API_ENDPOINTS.APP_TWMS_AVISO, { headers: { Authorization: `Bearer ${token}` } })
      const j = await r.json()
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`)
      setDados(j)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falhou')
    }
  }, [token])

  useEffect(() => { carregar() }, [carregar])

  const chamar = async (url: string, opcoes: RequestInit) => {
    setSalvando(true)
    setErro(null)
    try {
      const r = await fetch(url, {
        ...opcoes,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`)
      await carregar()
      return true
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falhou')
      return false
    } finally {
      setSalvando(false)
    }
  }

  // Enquanto não carregou, nada na tela: um cartão "carregando" no meio dos
  // números-chave só pisca.
  if (!dados) return null

  const ligado = dados.ligado
  const ativos = dados.numeros.filter(n => n.ativo)
  const resumo = ligado
    ? ativos.length
      ? `cada pedido novo chega em ${ativos.length === 1 ? '1 celular' : `${ativos.length} celulares`}, com os botões Aprovar · Nova senha · Reenviar`
      : 'ligado, mas ninguém na lista — nenhum aviso sai assim'
    : 'desligado — a fila se resolve só por aqui'

  return (
    <section className="glass-panel neu-card rounded-2xl px-4 py-3 flex flex-col gap-2.5">
      <div className="flex items-center gap-3 flex-wrap">
        <FiSmartphone className={`w-4 h-4 shrink-0 ${ligado ? 'text-emerald-600' : 'text-muted-foreground'}`} />
        <div className="flex-1 min-w-[14rem]">
          <h3 className="text-[12px] font-semibold">Aprovar pelo WhatsApp</h3>
          <p className={`text-[11px] ${ligado && !ativos.length ? 'text-amber-700' : 'text-muted-foreground'}`}>{resumo}</p>
        </div>
        <button
          type="button" role="switch" aria-checked={ligado} aria-label="Aprovar pelo WhatsApp"
          disabled={!podeGerir || salvando}
          onClick={() => chamar(API_ENDPOINTS.APP_TWMS_AVISO, { method: 'POST', body: JSON.stringify({ ligado: !ligado }) })}
          className={`relative h-6 w-11 rounded-full transition-colors disabled:opacity-40 ${ligado ? 'bg-emerald-600' : 'bg-foreground/20'}`}
        >
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${ligado ? 'left-[1.375rem]' : 'left-0.5'}`} />
        </button>
        <button type="button" onClick={() => setAberto(a => !a)} className="text-[11.5px] underline text-muted-foreground">
          {aberto ? 'fechar' : `quem recebe (${ativos.length})`}
        </button>
      </div>

      {aberto && (
        <div className="flex flex-col gap-2 border-t border-border/60 pt-2.5">
          {ativos.length === 0 ? (
            <p className="text-[11.5px] text-muted-foreground">Ninguém na lista ainda.</p>
          ) : ativos.map(n => (
            <div key={n.id} className="flex items-center gap-2 text-[12px]">
              <span className="font-medium">{n.nome || 'sem nome'}</span>
              <span className="tabular-nums text-muted-foreground">{formatPhone(n.numero)}</span>
              {podeGerir && (
                <button
                  type="button" disabled={salvando}
                  onClick={() => chamar(API_ENDPOINTS.APP_TWMS_AVISO_NUMERO(n.id), { method: 'DELETE' })}
                  aria-label={`Tirar ${n.nome || n.numero} da lista`}
                  className="ml-auto text-muted-foreground hover:text-red-600 disabled:opacity-40"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}

          {podeGerir && (
            <form
              onSubmit={async e => {
                e.preventDefault()
                const ok = await chamar(API_ENDPOINTS.APP_TWMS_AVISO_NUMEROS, {
                  method: 'POST', body: JSON.stringify({ nome, numero: soDigitos(numero) }),
                })
                if (ok) { setNome(''); setNumero('') }
              }}
              className="flex items-center gap-2 flex-wrap pt-1"
            >
              <input
                value={nome} onChange={e => setNome(e.target.value)} placeholder="nome" aria-label="Nome de quem recebe"
                className="rounded-lg border border-border bg-transparent px-2.5 py-1 text-[12px] outline-none w-32"
              />
              <input
                value={numero} onChange={e => setNumero(e.target.value)} inputMode="numeric"
                placeholder="DDD + número" aria-label="WhatsApp de quem recebe"
                className="rounded-lg border border-border bg-transparent px-2.5 py-1 text-[12px] outline-none w-40 tabular-nums"
              />
              <button
                type="submit" disabled={salvando || soDigitos(numero).length < 10}
                className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3 py-1.5 text-[11.5px] font-semibold disabled:opacity-40"
              >
                <FiPlus className="w-3.5 h-3.5" />incluir
              </button>
            </form>
          )}

          <p className="text-[11px] text-muted-foreground">
            Quem está na lista decide pelo celular: tocar em <b>Aprovar</b> manda a senha na hora, igual ao botão daqui,
            e fica gravado quem tocou. Só estes números valem — mensagem de qualquer outro é ignorada.
          </p>
        </div>
      )}

      {erro && <p className="text-[11.5px] text-red-600">{erro}</p>}
    </section>
  )
}
